import axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from 'axios';
import {revokeUser} from 'common/src/modules/auth/auth.service';
import _isNil from 'lodash/isNil';
import _omitBy from 'lodash/omitBy';
import _toPairs from 'lodash/toPairs';
import {ApiError} from '../errorHandling/errorHandling.types';
import RoutesData from '../routing/appRoutes';
import {PublicRoutes} from '../routing/routing.config';
import routingService from '../routing/routing.service';
import {StatusCode} from './api.consts';
import {ApiOptions, ApiResponse, ParamsObj, Payload, PostApiOptions, PostDataObj} from './apiTypes';
import {createParamObjWithMultiples} from './apiUtils';
import applyAuthorizationHeader from './utils/applyAuthorizationHeader';
import _omit from 'lodash/omit';
import {RequestCancelledException} from './api.exceptions';

function constructApiService({
  createApiUrl,
  handleResponse,
  handleError,
  useFormDataAsDefault = false,
}: {
  createApiUrl: ({endpoint, options}: {endpoint: string; options: ApiOptions}) => string;
  handleResponse: <G>(res: AxiosResponse) => Payload<G>;
  handleError: (error: AxiosError) => ApiError;
  useFormDataAsDefault?: boolean;
}) {
  async function executeGetRequest<T>(
    endpoint: string,
    params: ParamsObj = {},
    options: ApiOptions = {},
  ): Promise<Payload<T>> {
    const fullUrl = createApiUrl({endpoint, options});
    const config = createConfigObj(params, options);

    try {
      const response = await axios.get<ApiResponse<T>>(fullUrl, config);

      return handleResponse<T>(response);
    } catch (error) {
      await handleErrorResponse(error);
    }
  }

  async function executeDeleteRequest<T>(
    endpoint: string,
    params: ParamsObj = {},
    postDataObj: PostDataObj = {},
    options: PostApiOptions = {},
  ): Promise<Payload<T>> {
    const fullUrl = createApiUrl({endpoint, options});
    const config = createConfigObj(params, options);
    config.data = options.sendAsFormData
      ? new URLSearchParams(postDataObj as Record<string, string>).toString()
      : postDataObj;

    try {
      const response = await axios.delete<ApiResponse<T>>(fullUrl, config);

      return handleResponse<T>(response);
    } catch (error) {
      await handleErrorResponse(error);
    }
  }

  async function executePostRequest<T>(
    endpoint: string,
    paramsObj: ParamsObj = {},
    postDataObj: PostDataObj = {},
    options: PostApiOptions = {},
  ): Promise<Payload<T>> {
    const {fullUrl, data, config} = buildRequestParts({options, paramsObj, postDataObj, endpoint});
    config.signal = options.abortSignal;

    try {
      const response = await axios.post<ApiResponse<T>>(fullUrl, data, config);

      return handleResponse<T>(response);
    } catch (error) {
      if (axios.isCancel(error)) {
        throw new RequestCancelledException();
      }

      await handleErrorResponse(error);
    }
  }

  async function executePutRequest<T>(
    endpoint: string,
    paramsObj: ParamsObj = {},
    postDataObj: PostDataObj = {},
    options: PostApiOptions = {},
  ): Promise<Payload<T>> {
    const {fullUrl, data, config} = buildRequestParts({options, paramsObj, postDataObj, endpoint});

    try {
      const response = await axios.put<ApiResponse<T>>(fullUrl, data, config);

      return handleResponse<T>(response);
    } catch (error) {
      await handleErrorResponse(error);
    }
  }

  async function executePatchRequest<T>(
    endpoint: string,
    paramsObj: ParamsObj = {},
    postDataObj: PostDataObj = {},
    options: PostApiOptions = {},
  ): Promise<Payload<T>> {
    const {fullUrl, data, config} = buildRequestParts({options, paramsObj, postDataObj, endpoint});

    try {
      const response = await axios.patch<ApiResponse<T>>(fullUrl, data, config);

      return handleResponse<T>(response);
    } catch (error) {
      await handleErrorResponse(error);
    }
  }

  async function downloadFileRequest({
    endpoint,
    paramsObj = {},
    options = {},
    method = 'get',
  }: {
    endpoint: string;
    paramsObj?: ParamsObj;
    method?: 'get' | 'post';
    options?: ApiOptions;
  }) {
    const config: AxiosRequestConfig = {
      ...createConfigObj(paramsObj, options),
      responseType: 'arraybuffer',
    };

    const fullUrl = createApiUrl({endpoint, options});

    try {
      const response = await (method === 'get'
        ? axios.get<ArrayBuffer>(fullUrl, config)
        : axios.post<ArrayBuffer>(fullUrl, {}, config));

      return {data: response.data, headers: response.headers};
    } catch (error) {
      await handleErrorResponse(error);
    }
  }

  function createConfigObj(params: ParamsObj, options: PostApiOptions) {
    const paramsObj = options.doNotEncode ? params : createParamObjWithMultiples(params);

    const config: AxiosRequestConfig = {
      params: paramsObj,
      ...(options.skipAuth ? null : {withCredentials: true}),
      onUploadProgress: options.onUploadProgress,
      headers: {
        ...(options.headers && {...options.headers}),
        'Content-Type': options.sendAsFormData
          ? 'application/x-www-form-urlencoded; charset=UTF-8'
          : 'application/json',
      },
    };

    if (options.doNotEncode) {
      config.paramsSerializer = function paramsSerializer(paramsObj) {
        return _toPairs(paramsObj)
          .map(([key, value]) => `${key}=${value}`)
          .join('&');
      };
    }

    if (options.cancelToken) config.cancelToken = options.cancelToken;

    return options.skipAuth ? config : applyAuthorizationHeader(config);
  }

  function buildRequestParts({
    options,
    paramsObj,
    postDataObj,
    endpoint,
  }: {
    options: PostApiOptions;
    paramsObj: ParamsObj;
    postDataObj: Record<string, unknown>;
    endpoint: string;
  }) {
    options.sendAsFormData = options.sendAsFormData ?? useFormDataAsDefault;
    const config = createConfigObj(paramsObj, options);
    let data: Record<string, unknown> | FormData = postDataObj;

    if (options.sendAsFormData) {
      data = new FormData();

      for (const [key, value] of Object.entries(postDataObj || {})) {
        data.append(
          key,
          value instanceof File || typeof value === 'string' ? value : JSON.stringify(value),
        );
      }
    }

    const fullUrl = createApiUrl({endpoint, options});

    return {fullUrl, data, config};
  }

  async function uploadFile<T>({
    endpoint,
    params = {},
    postData = {},
    file,
    renameTo,
    options = {},
    formDataKey = 'file',
    method = 'post',
  }: {
    endpoint: string;
    params: ParamsObj;
    postData?: ParamsObj;
    file: File;
    renameTo?: string;
    options?: ApiOptions;
    formDataKey?: string;
    method?: 'post' | 'put';
  }): Promise<Payload<T>> {
    // Automatic serialization
    // Starting from v0.27.0, Axios supports automatic object serialization to a FormData object if the request Content-Type header is set to multipart/form-data.
    // The following request will submit the data in a FormData format

    // Create the configuration object, ensuring the Content-Type is handled by Axios for FormData
    const config = _omit(
      createConfigObj(params, {...options, sendAsFormData: true}),
      'Content-Type',
    );

    // Initialize a FormData object and append the file (with optional renaming)
    const formData = new FormData();
    formData.append(formDataKey, file, renameTo ?? file.name);

    // Append any additional postData fields to the FormData object
    for (const [key, value] of Object.entries(postData)) {
      formData.append(key, typeof value === 'string' ? value : JSON.stringify(value));
    }

    // Construct the full API URL including query parameters
    const fullUrl = createApiUrl({endpoint, options});

    try {
      const response = await axios[method]<ApiResponse<T>>(fullUrl, formData, config);

      return handleResponse<T>(response);
    } catch (error) {
      await handleErrorResponse(error);
    }
  }

  function cleanEmptyValues(params: ParamsObj): ParamsObj {
    return _omitBy(params, _isNil);
  }

  async function handleErrorResponse(error: AxiosError) {
    switch (error.response?.status) {
      case StatusCode.UNAUTHORIZED:
        revokeUser();

        const {pathname, search} = window.location;

        if (pathname === RoutesData.Login) throw error;
        if (PublicRoutes.some((route) => routingService.isMatchRoute(route, location.pathname)))
          return;

        const params = new URLSearchParams(search);

        routingService.goTo({
          url: RoutesData.Login,
          reload: true,
          queryParams: {
            ...Object.fromEntries(params.entries()),
          },
        });

      default:
        throw handleError(error);
    }
  }

  return {
    executeGetRequest,
    executePatchRequest,
    executePostRequest,
    executePutRequest,
    executeDeleteRequest,
    downloadFileRequest,
    uploadFile,
    cleanEmptyValues,
    createApiUrl,
    buildRequestParts,
  };
}

export default constructApiService;
